import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import api from '../../utils/api';

const LeadSelectTable = ({ selectedLeads, setSelectedLeads }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await api('/leads');
      if (!res.ok) throw new Error('Failed to fetch leads');
      const data = await res.json();
      // The API might return { data: leads } or just the array depending on the pagination implementation.
      const leadsData = Array.isArray(data) ? data : (data.data || data.leads || []);
      setLeads(leadsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const filteredLeads = leads.filter(lead => 
    lead.name?.toLowerCase().includes(search.toLowerCase()) || 
    lead.phone?.includes(search)
  );

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );

  const isAllSelected = paginatedLeads.length > 0 && paginatedLeads.every(l => selectedLeads.includes(l._id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const pageIds = paginatedLeads.map(l => l._id);
      const newSelected = new Set([...selectedLeads, ...pageIds]);
      setSelectedLeads(Array.from(newSelected));
    } else {
      const pageIds = paginatedLeads.map(l => l._id);
      setSelectedLeads(selectedLeads.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedLeads.includes(id)) {
      setSelectedLeads(selectedLeads.filter(leadId => leadId !== id));
    } else {
      setSelectedLeads([...selectedLeads, id]);
    }
  };

  return (
    <div style={{ background: 'var(--color-background-primary)', borderRadius: '8px', border: '1px solid var(--color-border-primary)' }}>
      <div style={{ padding: '16px', borderBottom: '1px solid var(--color-border-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ position: 'relative', width: '300px' }}>
          <Search size={16} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: '12px', top: '10px' }} />
          <input 
            type="text" 
            placeholder="Search leads by name or phone..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              border: '1px solid var(--color-border-primary)',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
        <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-secondary)' }}>
          {selectedLeads.length} leads selected
        </div>
      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading leads...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ background: 'var(--color-background-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', width: '40px' }}>
                  <input 
                    type="checkbox" 
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Name</th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Phone</th>
                <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-primary)', fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No leads found</td>
                </tr>
              ) : (
                paginatedLeads.map(lead => (
                  <tr key={lead._id} style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <input 
                        type="checkbox" 
                        checked={selectedLeads.includes(lead._id)}
                        onChange={() => handleSelectOne(lead._id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--color-text-primary)' }}>{lead.name}</td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>{lead.phone}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ 
                        fontSize: '12px', 
                        background: 'var(--color-background-tertiary)', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {lead.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Footer */}
      {!loading && filteredLeads.length > 0 && (
        <div style={{ 
          padding: '12px 16px', 
          borderTop: '1px solid var(--color-border-primary)', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: 'var(--color-background-secondary)',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px'
        }}>
          <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredLeads.length)} of {filteredLeads.length} leads
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--color-border-primary)',
                  background: currentPage === 1 ? 'var(--color-background-tertiary)' : 'white',
                  color: currentPage === 1 ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                  borderRadius: '6px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Previous Page
              </button>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--color-border-primary)',
                  background: currentPage === totalPages ? 'var(--color-background-tertiary)' : 'white',
                  color: currentPage === totalPages ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                  borderRadius: '6px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Next Page
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadSelectTable;
